'use client'

import { Component } from 'react'
import type { ReactNode } from 'react'
import type {
  InstrumentationProvider,
  ObservabilityContext,
} from '../core/Instrumentation'

interface ErrorBoundaryProps {
  children: ReactNode
  instrumentation: InstrumentationProvider
  fallback?:
    | ReactNode
    | ((error: Error, errorInfo: React.ErrorInfo) => ReactNode)
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { instrumentation, onError } = this.props

    const context: ObservabilityContext = {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    }

    instrumentation.captureError(error, context)
    instrumentation.log(
      'error',
      `ErrorBoundary caught an error: ${error.message}`,
      context,
    )

    if (onError) {
      onError(error, errorInfo)
    }

    this.setState({ error, errorInfo })
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (hasError) {
      if (typeof fallback === 'function' && error && errorInfo) {
        return fallback(error, errorInfo)
      }

      if (fallback && typeof fallback !== 'function') {
        return fallback
      }

      // TODO: add fallback error
      return null
    }

    return children
  }
}
